import { Card } from 'components/Card';
import { ModalScreenProps, ModalScreens } from 'components/Navigation/types';
import { Page } from 'components/Page';
import { useConfirmUnsaved } from 'hooks/useConfirm';
import { useEsc } from 'hooks/useEsc';
import { useForceUpdate } from 'hooks/useForceUpdate';
import { cloneDeep, isEqual, sortBy, toInteger } from 'lodash';
import React from 'react';
import { useDispatch } from 'react-redux';
import { NAVIGATE_MODAL_BACK, doSettingsAseCheckSave } from 'store/actions';
import { AssessmentCheck } from 'store/remote/types';
import {
  selectRemoteComplianceSubtypesWithType,
  selectRemoteSettingsAssessmentCheck,
} from 'store/remote/selectors';
import { useSelector } from 'hooks/useSelector';
import { CardRow } from 'components/CardRow';
import { OptionField } from 'components/Field/OptionField';
import { PageHeader } from 'components/PageHeader';

export const CheckModal: React.FC<
  ModalScreenProps<ModalScreens.SettingsAseCheck>
> = ({ navigation, route }) => {
  const { id } = route.params;
  const dispatch = useDispatch();
  const confirmUnsaved = useConfirmUnsaved();
  const forceUpdate = useForceUpdate();
  const isDirtyRef = React.useRef(false);

  const check = useSelector((state) =>
    selectRemoteSettingsAssessmentCheck(state, id),
  );
  const complianceSubtypes = useSelector(
    selectRemoteComplianceSubtypesWithType,
  );

  const dataRef = React.useRef<AssessmentCheck>(
    cloneDeep(
      check || {
        id: '',
        name: '',
        assessmentTypeId: null,
        field: {
          section: { name: '' },
          group: { name: '' },
        },
        type: {
          id: '',
          name: '',
        },
      },
    ),
  );

  const checkIsDirty = React.useCallback(
    () => !!check && !isEqual(check, dataRef.current),
    [check],
  );
  isDirtyRef.current = checkIsDirty();

  const complianceOptions = React.useMemo(
    () =>
      sortBy(Object.values(complianceSubtypes), [
        'complianceName',
        'complianceSubName',
        'name',
      ]).map((c) => ({
        name: c.name,
        internalValue: `${c.id}`,
      })),
    [complianceSubtypes],
  );

  const setValue = React.useCallback(
    (
      changes: Partial<typeof dataRef.current>,
      { doForceUpdate }: { doForceUpdate?: 'always' | 'dirtyChange' },
    ) => {
      const newValues = { ...dataRef.current, ...changes };
      if (!isEqual(dataRef.current, newValues)) {
        dataRef.current = { ...dataRef.current, ...changes };
        if (
          doForceUpdate === 'always' ||
          (doForceUpdate === 'dirtyChange' &&
            checkIsDirty() !== isDirtyRef.current)
        ) {
          forceUpdate();
        } else {
          isDirtyRef.current = checkIsDirty();
        }
      }
    },
    [checkIsDirty, forceUpdate],
  );

  const handleSave = React.useCallback(() => {
    if (isDirtyRef.current) {
      dispatch(doSettingsAseCheckSave(dataRef.current));
    } else {
      dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SettingsAseCheck]));
    }
  }, [dispatch]);

  const handleClose = React.useCallback(() => {
    if (isDirtyRef.current) {
      confirmUnsaved({
        onSave: handleSave,
        onDiscard: () =>
          dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SettingsAseCheck])),
      });
      return;
    }
    dispatch(NAVIGATE_MODAL_BACK());
  }, [confirmUnsaved, dispatch, handleSave]);

  const isDirtyVar = isDirtyRef.current;
  React.useEffect(() => {
    navigation?.setOptions({ gestureEnabled: !isDirtyVar });
  }, [navigation, isDirtyVar]);
  useEsc(handleClose);

  React.useEffect(() => {
    if (!check) {
      dispatch(NAVIGATE_MODAL_BACK([ModalScreens.SettingsAseCheck]));
    }
  }, [check, dispatch]);

  if (!check) {
    return null;
  }

  return (
    <Page
      scrollView
      header={
        <PageHeader
          title="Check"
          onPressCancelButton={handleClose}
          onPressSaveButton={handleSave}
        />
      }
    >
      <Card label="Check">
        <CardRow value={dataRef.current.name} />
      </Card>
      <Card
        label="Assessment Type"
        explanation="RiskBase will automatically create tasks to complete assessments where required"
      >
        <OptionField
          expandedInlineFirst
          expandedInlineLast
          showNoValueOption
          modalLabel="Assessment Type"
          value={
            dataRef.current.assessmentTypeId
              ? [`${dataRef.current.assessmentTypeId}`]
              : []
          }
          onChange={(newValues) =>
            setValue(
              {
                assessmentTypeId: newValues.length
                  ? toInteger(newValues[0]!)
                  : null,
              },
              { doForceUpdate: 'always' },
            )
          }
          noValueOption={{
            name: 'No Assessment Type',
          }}
          options={complianceOptions}
        />
      </Card>
    </Page>
  );
};
